// ========================================
// APPLICATION CONFIGURATION
// Centralized configuration with security checks
// ========================================

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

// ========================================
// API BASE URL WITH HTTPS ENFORCEMENT
// ========================================

/**
 * Get and validate API base URL
 * - Development: Uses proxy (configured in package.json)
 * - Production: Requires REACT_APP_API_BASE_URL with HTTPS
 */
function getApiBaseUrl() {
  const configuredUrl = process.env.REACT_APP_API_BASE_URL || '';

  // In production, enforce HTTPS
  if (IS_PRODUCTION && configuredUrl) {
    // Validate HTTPS in production
    if (!configuredUrl.startsWith('https://')) {
      console.error(
        '🔒 SECURITY WARNING: API_BASE_URL must use HTTPS in production!',
        'Current URL:', configuredUrl
      );

      // Attempt to upgrade to HTTPS if possible
      if (configuredUrl.startsWith('http://')) {
        const httpsUrl = configuredUrl.replace('http://', 'https://');
        console.warn('Attempting to upgrade to HTTPS:', httpsUrl);
        return httpsUrl;
      }
    }
  }

  return configuredUrl;
}

export const API_BASE_URL = getApiBaseUrl();

// ========================================
// SECURITY CONFIGURATION
// ========================================

export const SECURITY_CONFIG = {
  // Enforce HTTPS for all external requests in production
  REQUIRE_HTTPS: IS_PRODUCTION,

  // Content Security Policy directives (for reference)
  CSP_DIRECTIVES: {
    'default-src': ["'self'"],
    'script-src': ["'self'"],
    'style-src': ["'self'", "'unsafe-inline'"], // MUI requires inline styles
    'img-src': ["'self'", 'data:', 'https:'],
    'connect-src': ["'self'", API_BASE_URL].filter(Boolean),
    'font-src': ["'self'"],
    'frame-ancestors': ["'none'"],
  },

  // Session configuration
  SESSION: {
    // Time before token expiry to trigger refresh (in milliseconds)
    REFRESH_BUFFER_MS: 2 * 60 * 1000, // 2 minutes
    // Interval to check for token expiry
    CHECK_INTERVAL_MS: 30 * 1000, // 30 seconds
  },
};

// ========================================
// FEATURE FLAGS
// ========================================

export const FEATURE_FLAGS = {
  // Enable debug logging (automatically disabled in production)
  DEBUG_LOGGING: IS_DEVELOPMENT || process.env.REACT_APP_DEBUG === 'true',

  // Enable auth debug logging
  DEBUG_AUTH: IS_DEVELOPMENT && process.env.REACT_APP_DEBUG_AUTH === 'true',

  // Enable performance monitoring
  PERFORMANCE_MONITORING: IS_PRODUCTION,

  // Enable error reporting
  ERROR_REPORTING: IS_PRODUCTION,

  // Enable offline support
  OFFLINE_SUPPORT: true,

  // Enable lazy loading
  LAZY_LOADING: true,
};

// ========================================
// APPLICATION CONSTANTS
// ========================================

export const APP_CONFIG = {
  NAME: 'Questguru',
  VERSION: process.env.REACT_APP_VERSION || '1.0.0',

  // Pagination defaults
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,

  // File upload limits
  MAX_FILE_SIZE_MB: 10,
  ALLOWED_FILE_TYPES: ['.json', '.txt', '.csv'],

  // Question paper limits
  MAX_QUESTIONS_PER_PAPER: 200,
  MIN_QUESTIONS_PER_PAPER: 1,

  // Rate limiting (frontend awareness)
  RATE_LIMIT: {
    REQUESTS_PER_MINUTE: 60,
    AUTH_REQUESTS_PER_MINUTE: 5,
  },
};

// ========================================
// RUNTIME SECURITY CHECKS
// ========================================

/**
 * Validate current environment security
 * Called on app initialization
 */
export function validateSecurityConfig() {
  const warnings = [];
  const errors = [];

  // Check HTTPS in production
  if (IS_PRODUCTION) {
    if (typeof window !== 'undefined' && window.location.protocol !== 'https:') {
      errors.push('Application must be served over HTTPS in production');
    }

    if (API_BASE_URL && !API_BASE_URL.startsWith('https://')) {
      errors.push('API_BASE_URL must use HTTPS in production');
    }
  }

  // Warn about debug flags in production
  if (IS_PRODUCTION && FEATURE_FLAGS.DEBUG_LOGGING) {
    warnings.push('Debug logging is enabled in production');
  }

  // Log results
  if (errors.length > 0) {
    console.error('🚨 Security configuration errors:', errors);
  }

  if (warnings.length > 0 && IS_DEVELOPMENT) {
    console.warn('⚠️ Security configuration warnings:', warnings);
  }

  return { valid: errors.length === 0, errors, warnings };
}

// Run validation on module load (non-blocking)
if (typeof window !== 'undefined') {
  // Delay to not block initial render
  setTimeout(() => validateSecurityConfig(), 0);
}

